"use client";

import { useState, useEffect, useCallback } from "react";
import { Wand2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import html2canvas from "html2canvas-pro";

interface ElementData {
  tagName: string;
  elementId?: string;
  className?: string;
  textContent?: string;
  attributes: Record<string, string>;
  xpath: string;
  screenshot?: string;
  htmlSnippet?: string;
  boundingBox: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
}

interface MagicWandSelectorProps {
  isActive: boolean;
  onElementSelect: (elementData: ElementData) => void;
  onCancel: () => void;
}

export function MagicWandSelector({
  isActive,
  onElementSelect,
  onCancel,
}: MagicWandSelectorProps) {
  const [highlightedElement, setHighlightedElement] =
    useState<HTMLElement | null>(null);
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({});

  const getXPath = (element: HTMLElement): string => {
    if (element.id) {
      return `//*[@id="${element.id}"]`;
    }

    const parts: string[] = [];
    let current: HTMLElement | null = element;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let index = 0;
      let sibling: Element | null = current;

      while (sibling) {
        if (
          sibling.nodeType === Node.ELEMENT_NODE &&
          sibling.nodeName === current.nodeName
        ) {
          index++;
        }
        sibling = sibling.previousElementSibling;
      }

      const tagName = current.nodeName.toLowerCase();
      const part = index > 1 ? `${tagName}[${index}]` : tagName;
      parts.unshift(part);

      current = current.parentElement;
    }

    return "/" + parts.join("/");
  };

  const getElementData = async (element: HTMLElement): Promise<ElementData> => {
    const rect = element.getBoundingClientRect();
    const attributes: Record<string, string> = {};

    Array.from(element.attributes).forEach((attr) => {
      attributes[attr.name] = attr.value;
    });

    // Capture screenshot of the element
    let screenshot: string | undefined;
    try {
      const canvas = await html2canvas(element, {
        allowTaint: true,
        useCORS: true,
        logging: false,
        scale: 2,
        backgroundColor: null,
      });
      screenshot = canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Element screenshot capture failed:", error);
    }

    // Get HTML snippet (outerHTML of the element)
    const htmlSnippet = element.outerHTML;

    return {
      tagName: element.tagName,
      elementId: element.id || undefined,
      className: element.className || undefined,
      textContent: element.textContent?.trim().substring(0, 200) || undefined,
      attributes,
      xpath: getXPath(element),
      screenshot,
      htmlSnippet,
      boundingBox: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      },
    };
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isActive) return;

      const element = e.target as HTMLElement;

      // Avoid selecting the selector UI itself
      if (element.closest("[data-magic-wand]")) {
        return;
      }

      setHighlightedElement(element);

      const rect = element.getBoundingClientRect();
      setOverlayStyle({
        position: "fixed",
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        border: "3px solid #8b5cf6",
        backgroundColor: "rgba(139, 92, 246, 0.1)",
        pointerEvents: "none",
        zIndex: 10000,
        transition: "all 0.1s ease",
      });
    },
    [isActive]
  );

  const handleClick = useCallback(
    async (e: MouseEvent) => {
      if (!isActive || !highlightedElement) return;

      e.preventDefault();
      e.stopPropagation();

      // Avoid selecting the selector UI itself
      if ((e.target as HTMLElement).closest("[data-magic-wand]")) {
        return;
      }

      const elementData = await getElementData(highlightedElement);
      onElementSelect(elementData);
    },
    [isActive, highlightedElement, onElementSelect]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      }
    },
    [onCancel]
  );

  useEffect(() => {
    if (isActive) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("click", handleClick, true);
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.cursor = "crosshair";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("click", handleClick, true);
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.cursor = "default";
      };
    }
  }, [isActive, handleMouseMove, handleClick, handleKeyDown]);

  if (!isActive) return null;

  return (
    <>
      {/* Highlight Overlay */}
      <div style={overlayStyle} />

      {/* Instructions Bar */}
      <div
        data-magic-wand
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] bg-background/95 backdrop-blur border-2 border-purple-500 rounded-lg shadow-lg px-6 py-3 flex items-center gap-4 animate-in slide-in-from-top-2"
      >
        <Wand2 className="h-5 w-5 text-purple-500 animate-pulse" />
        <div className="text-sm">
          <span className="font-semibold">Magic Wand Active:</span> Click on any
          element to select it, or press{" "}
          <kbd className="px-2 py-1 bg-muted rounded text-xs">ESC</kbd> to
          cancel
        </div>
        <Button size="sm" variant="ghost" onClick={onCancel} data-magic-wand>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </>
  );
}
